import type { DragEndEvent } from '@dnd-kit/core'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import TextLink from 'LuxKit/TextLink'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Divider from 'LuxKit/Divider'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useAppSelector } from 'hooks/reduxHooks'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import {
  getStripeOffSessionCardsByRegionAndPurposeId,
  removeStripeOffSessionCardByRegionAndPurposeId,
  updateStripeOffSessionCardPrioritiesByRegionAndPurposeId,
  type LuxPlusOffSessionPaymentCard,
} from 'api/payment'
import LuxPlusBackupPaymentMethodRow from './LuxPlusBackupPaymentMethodRow'

const BackupCardsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
`

const AddLink = styled(TextLink)`
  display: inline-flex;
  align-items: center;
`

interface Props {
  item: App.MemberSubscriptionItem
  onAddBackupPaymentMethod?: () => void
  renewalDate?: string
}

function LuxPlusBackupPaymentMethods({ item, onAddBackupPaymentMethod, renewalDate }: Props) {
  const intl = useIntl()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const [cards, setCards] = useState<Array<LuxPlusOffSessionPaymentCard>>([])
  const [primaryPaymentMethod, setPrimaryPaymentMethod] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)
  const [removingCardId, setRemovingCardId] = useState<string>()
  const [isReordering, setIsReordering] = useState(false)
  const [orderedBackupCardIds, setOrderedBackupCardIds] = useState<Array<string>>([])
  const [errorMessage, setErrorMessage] = useState<string>()
  const fetchCardsRequestRef = useRef(0)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  )

  const fetchCards = useCallback(() => {
    const requestId = fetchCardsRequestRef.current + 1
    fetchCardsRequestRef.current = requestId
    setIsLoading(true)
    setErrorMessage(undefined)

    getStripeOffSessionCardsByRegionAndPurposeId(regionCode, item.id).then(({ cards, paymentMethod }) => {
      if (fetchCardsRequestRef.current !== requestId) return

      setCards(cards)
      setPrimaryPaymentMethod(paymentMethod)
    }).catch(() => {
      if (fetchCardsRequestRef.current !== requestId) return

      setCards([])
      setPrimaryPaymentMethod(undefined)
      setErrorMessage(intl.formatMessage({
        id: 'bpqsxfeBU5scoO375n-K',
        defaultMessage: 'We couldn\'t load backup payment methods. Please try again.',
        description: 'Error message shown when LuxPlus+ backup payment methods fail to load',
      }))
    }).finally(() => {
      if (fetchCardsRequestRef.current !== requestId) return

      setIsLoading(false)
    })
  }, [intl, item.id, regionCode])

  useEffect(() => {
    fetchCards()
  }, [fetchCards])

  const backupCards = useMemo(() => (
    cards
      .filter(card => primaryPaymentMethod ? card.paymentMethod !== primaryPaymentMethod : card.priority !== 0)
      .sort((left, right) => (left.priority ?? Number.MAX_SAFE_INTEGER) - (right.priority ?? Number.MAX_SAFE_INTEGER))
  ), [cards, primaryPaymentMethod])

  useEffect(() => {
    if (isLoading || isReordering) return

    setOrderedBackupCardIds(backupCards.map(card => card.id))
  }, [backupCards, isLoading, isReordering])

  const backupCardsById = useMemo(() => new Map(backupCards.map(card => [card.id, card])), [backupCards])

  const displayBackupCards = useMemo(() => {
    const orderedBackupCards = orderedBackupCardIds
      .map(cardId => backupCardsById.get(cardId))
      .filter((card): card is LuxPlusOffSessionPaymentCard => !!card)
    const orderedIds = new Set(orderedBackupCards.map(card => card.id))
    const missingBackupCards = backupCards.filter(card => !orderedIds.has(card.id))

    return [...orderedBackupCards, ...missingBackupCards]
  }, [backupCards, backupCardsById, orderedBackupCardIds])

  const displayBackupCardIds = useMemo(() => displayBackupCards.map(card => card.id), [displayBackupCards])

  const isMutating = !!removingCardId || isReordering

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id || isMutating) return

    const oldIndex = displayBackupCardIds.indexOf(active.id as string)
    const newIndex = displayBackupCardIds.indexOf(over.id as string)
    if (oldIndex < 0 || newIndex < 0) return

    const nextCardIds = arrayMove(displayBackupCardIds, oldIndex, newIndex)
    setOrderedBackupCardIds(nextCardIds)
    setErrorMessage(undefined)
    setIsReordering(true)

    updateStripeOffSessionCardPrioritiesByRegionAndPurposeId(regionCode, item.id, nextCardIds).then(() => {
      fetchCards()
    }).catch(() => {
      setOrderedBackupCardIds(backupCards.map(card => card.id))
      setErrorMessage(intl.formatMessage({
        id: 'd2ZoeHSv8xcCfOdMmtKl',
        defaultMessage: 'We couldn\'t reorder backup payment methods. Please try again.',
        description: 'Error message shown when reordering LuxPlus+ backup payment methods fails',
      }))
    }).finally(() => {
      setIsReordering(false)
    })
  }, [backupCards, displayBackupCardIds, fetchCards, intl, isMutating, item.id, regionCode])

  const handleRemove = useCallback((cardId: string) => {
    setErrorMessage(undefined)
    setRemovingCardId(cardId)

    removeStripeOffSessionCardByRegionAndPurposeId(regionCode, item.id, cardId).then(() => {
      fetchCards()
    }).catch(() => {
      setErrorMessage(intl.formatMessage({
        id: '1Sh68Isq4vTiMwuDqXPq',
        defaultMessage: 'We couldn\'t remove that backup payment method. Please try again.',
        description: 'Error message shown when removing a LuxPlus+ backup payment method fails',
      }))
    }).finally(() => {
      setRemovingCardId(undefined)
    })
  }, [fetchCards, intl, item.id, regionCode])

  return <VerticalSpacer gap={12}>
    <VerticalSpacer gap={2}>
      <BodyText variant="medium" weight="bold">
        {intl.formatMessage({
          id: '3DbfEChK3DlHOTswPVKo',
          defaultMessage: 'Backup payment methods',
          description: 'Section heading for backup payment methods in LuxPlus+ account management',
        })}
      </BodyText>
      <BodyText variant="small">
        {intl.formatMessage({
          id: 'FVmMgWNE6zOKw-RLYY1U',
          defaultMessage: "If your primary card fails at LuxPlus+ renewal, we'll charge a backup method so your membership stays active.",
          description: 'Description of backup payment methods in LuxPlus+ account management',
        })}
      </BodyText>
    </VerticalSpacer>

    {isLoading && <TextLoadingBox width="100%" />}

    {!!errorMessage && <MessageBanner kind="critical" density="compact">{errorMessage}</MessageBanner>}

    {!isLoading && displayBackupCards.length === 0 && <BodyText variant="small">
      {intl.formatMessage({
        id: '8BHHin2bMgkSN1PIMys9',
        defaultMessage: 'No backup payment methods added',
        description: 'Empty state text when a LuxPlus+ member has no backup payment methods',
      })}
    </BodyText>}

    {!isLoading && displayBackupCards.length > 0 && <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={displayBackupCardIds} strategy={verticalListSortingStrategy}>
        <BackupCardsList>
          {displayBackupCards.map((offSessionCard, index) => <LuxPlusBackupPaymentMethodRow
            key={offSessionCard.id}
            isRemoveDisabled={isMutating}
            isRemoving={removingCardId === offSessionCard.id}
            isReorderDisabled={isMutating}
            offSessionCard={offSessionCard}
            onRemove={handleRemove}
            position={index + 1}
            renewalDate={renewalDate}
          />)}
        </BackupCardsList>
      </SortableContext>
    </DndContext>}

    <AddLink size="caption" onClick={onAddBackupPaymentMethod} disabled={isMutating} startIcon={<LinePlusIcon size="S" />}>
      {intl.formatMessage({
        id: 'yUBorvPsbdSYCY8S27Ie',
        defaultMessage: 'Add',
        description: 'Link text to add a LuxPlus+ backup payment method from the membership modal',
      })}
    </AddLink>
    <Divider kind="primary" />
  </VerticalSpacer>
}

export default LuxPlusBackupPaymentMethods
